import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Instagram, ChevronLeft, ChevronRight, Menu, Play, X, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BridalServices() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentBrideSlide, setCurrentBrideSlide] = useState(0);
  const [currentPriceSlide, setCurrentPriceSlide] = useState(0);
  const [activeCategory, setActiveCategory] = useState("BRIDAL");
  const [heroImages, setHeroImages] = useState([]);
  const [lastBrides, setLastBrides] = useState([]);
  const [inspiringVideo, setInspiringVideo] = useState("");
  const [priceListImage, setPriceListImage] = useState("");
  const [priceListHeadings, setPriceListHeadings] = useState([]);
  const [brideStories, setBrideStories] = useState([]);
  const [currentStorySlide, setCurrentStorySlide] = useState(0);
  const [fullscreenBride, setFullscreenBride] = useState(null);
  const [fullscreenStoryVideo, setFullscreenStoryVideo] = useState(null);
  
  // New sections state
  const [inspiringBrideLooks, setInspiringBrideLooks] = useState([]);
  const [currentInspiringSlide, setCurrentInspiringSlide] = useState(0);
  const [makeupProcessVideo, setMakeupProcessVideo] = useState("");
  const [expandedProcess, setExpandedProcess] = useState(null);
  const [artistImages, setArtistImages] = useState([]);
  const [currentArtistSlide, setCurrentArtistSlide] = useState(0);
  const [artistBio, setArtistBio] = useState({ intro: "", outro: "" });

  const categories = ["BRIDAL", "RECEPTION", "ENGAGEMENT"];

  // Placeholder hero slides with bride name and date
  const placeholderSlides = [
    { id: 1, image: "", brideName: "PRIYA SHARMA", makeupDate: "15 January 2025" },
    { id: 2, image: "", brideName: "ANANYA SINGH", makeupDate: "22 January 2025" },
    { id: 3, image: "", brideName: "RIYA GUPTA", makeupDate: "28 January 2025" },
    { id: 4, image: "", brideName: "SNEHA PATEL", makeupDate: "5 February 2025" },
    { id: 5, image: "", brideName: "KAVYA MISHRA", makeupDate: "12 February 2025" },
    { id: 6, image: "", brideName: "ISHITA RAO", makeupDate: "18 February 2025" },
    { id: 7, image: "", brideName: "ADITI KUMAR", makeupDate: "25 February 2025" },
    { id: 8, image: "", brideName: "MEERA JOSHI", makeupDate: "2 March 2025" },
  ];

  // Placeholder last 5 brides
  const placeholderBrides = [
    { id: 1, image: "", brideName: "PRIYA", makeupDate: "15 Jan 2025" },
    { id: 2, image: "", brideName: "ANANYA", makeupDate: "22 Jan 2025" },
    { id: 3, image: "", brideName: "RIYA", makeupDate: "28 Jan 2025" },
    { id: 4, image: "", brideName: "SNEHA", makeupDate: "5 Feb 2025" },
    { id: 5, image: "", brideName: "KAVYA", makeupDate: "12 Feb 2025" },
  ];

  // Placeholder price list headings with items
  const placeholderHeadings = [
    {
      id: "1",
      name: "BRIDAL PACKAGES",
      items: [
        { id: 1, name: "Bridal Makeup", detail: "Complete bridal look with HD makeup", price: "25,000" },
        { id: 2, name: "Reception Look", detail: "Glamorous evening makeup with styling", price: "15,000" },
        { id: 3, name: "Mehendi Function", detail: "Traditional look with soft makeup", price: "8,000" },
      ]
    },
    {
      id: "2",
      name: "PARTY MAKEUP",
      items: [
        { id: 4, name: "Engagement Makeup", detail: "Elegant makeup for your special day", price: "12,000" },
        { id: 5, name: "Party Makeup", detail: "Glamorous party ready look", price: "5,000" },
        { id: 6, name: "Cocktail Look", detail: "Chic cocktail party makeup", price: "7,000" },
      ]
    },
    {
      id: "3",
      name: "PRE-WEDDING",
      items: [
        { id: 7, name: "Pre-Wedding Shoot", detail: "Camera-ready makeup for photoshoot", price: "10,000" },
        { id: 8, name: "Sangeet Look", detail: "Vibrant makeup for sangeet night", price: "8,000" },
      ]
    }
  ];

  // Derived states
  const displayHeadings = priceListHeadings.length > 0 ? priceListHeadings : placeholderHeadings;
  const currentHeading = displayHeadings[currentPriceSlide] || displayHeadings[0];
  const slides = heroImages.length > 0 
    ? heroImages.map(img => ({
        ...img,
        brideName: img.brideName || img.title || "BRIDE NAME",
        makeupDate: img.makeupDate || img.subtitle || "Makeup Date"
      }))
    : placeholderSlides;
  const brides = lastBrides.length > 0 ? lastBrides : placeholderBrides;

  // Data fetching on mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch hero images
      try {
        const response = await fetch(`${API}/bridal-hero-images`);
        const data = await response.json();
        if (data.images && data.images.length > 0) {
          setHeroImages(data.images);
        }
      } catch (error) {
        console.error("Error fetching hero images:", error);
      }

      // Fetch last brides
      try {
        const response = await fetch(`${API}/last-brides`);
        const data = await response.json();
        if (data.brides && data.brides.length > 0) {
          setLastBrides(data.brides);
        }
      } catch (error) {
        console.error("Error fetching last brides:", error);
      }

      // Fetch inspiring video
      try {
        const response = await fetch(`${API}/inspiring-video`);
        const data = await response.json();
        if (data.video) {
          setInspiringVideo(data.video);
        }
      } catch (error) {
        console.error("Error fetching inspiring video:", error);
      }

      // Fetch price list
      try {
        const response = await fetch(`${API}/price-list`);
        const data = await response.json();
        if (data.image) setPriceListImage(data.image);
        if (data.headings && data.headings.length > 0) setPriceListHeadings(data.headings);
      } catch (error) {
        console.error("Error fetching price list:", error);
      }

      // Fetch bride stories
      try {
        const response = await fetch(`${API}/bride-stories`);
        const data = await response.json();
        if (data.stories && data.stories.length > 0) setBrideStories(data.stories);
      } catch (error) {
        console.error("Error fetching bride stories:", error);
      }

      // Fetch inspiring bride looks
      try {
        const response = await fetch(`${API}/inspiring-bride-looks`);
        const data = await response.json();
        if (data.looks && data.looks.length > 0) setInspiringBrideLooks(data.looks);
      } catch (error) {
        console.error("Error fetching inspiring bride looks:", error);
      }

      // Fetch makeup process video
      try {
        const response = await fetch(`${API}/makeup-process-video`);
        const data = await response.json();
        if (data.video) setMakeupProcessVideo(data.video);
      } catch (error) {
        console.error("Error fetching makeup process video:", error);
      }

      // Fetch artist data
      try {
        const response = await fetch(`${API}/artist-data`);
        const data = await response.json();
        if (data.images && data.images.length > 0) setArtistImages(data.images);
        if (data.intro) setArtistBio(prev => ({ ...prev, intro: data.intro }));
        if (data.outro) setArtistBio(prev => ({ ...prev, outro: data.outro }));
      } catch (error) {
        console.error("Error fetching artist data:", error);
      }
    };
    fetchData();
  }, []);

  // Placeholder data for inspiring bride looks
  const placeholderInspiringLooks = [
    { id: "1", image: "", name: "PRIYA" },
    { id: "2", image: "", name: "ANANYA" },
    { id: "3", image: "", name: "RIYA" },
    { id: "4", image: "", name: "SNEHA" },
    { id: "5", image: "", name: "KAVYA" },
    { id: "6", image: "", name: "ISHITA" },
  ];
  const displayInspiringLooks = inspiringBrideLooks.length > 0 ? inspiringBrideLooks : placeholderInspiringLooks;

  // Auto-advance inspiring bride looks (continuous rolling)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentInspiringSlide((prev) => (prev + 1) % displayInspiringLooks.length);
    }, 2000);
    return () => clearInterval(timer);
  }, [displayInspiringLooks.length]);

  // Auto-advance hero slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Auto-advance bride slides one by one
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBrideSlide((prev) => (prev + 1) % brides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [brides.length]);

  // Auto-advance price slides
  useEffect(() => {
    if (displayHeadings.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentPriceSlide((prev) => (prev + 1) % displayHeadings.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [displayHeadings.length]);

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const nextBrideSlide = () => {
    setCurrentBrideSlide((prev) => (prev + 1) % brides.length);
  };

  const prevBrideSlide = () => {
    setCurrentBrideSlide((prev) => (prev - 1 + brides.length) % brides.length);
  };

  const nextPriceSlide = () => {
    setCurrentPriceSlide((prev) => (prev + 1) % displayHeadings.length);
  };

  const prevPriceSlide = () => {
    setCurrentPriceSlide((prev) => (prev - 1 + displayHeadings.length) % displayHeadings.length);
  };

  // Bride Stories navigation (manual only, max 6 slides, loops)
  const placeholderStories = [
    { id: "1", video: "", fullVideo: "", brideName: "PRIYA", makeupDate: "15 Jan 2025", subtitle: "A beautiful journey of love and celebration" },
    { id: "2", video: "", fullVideo: "", brideName: "ANANYA", makeupDate: "22 Jan 2025", subtitle: "The perfect bridal transformation" },
    { id: "3", video: "", fullVideo: "", brideName: "RIYA", makeupDate: "28 Jan 2025", subtitle: "Making dreams come true on her special day" },
  ];
  
  const displayStories = brideStories.length > 0 ? brideStories.slice(0, 6) : placeholderStories;
  const currentStory = displayStories[currentStorySlide] || displayStories[0];

  const nextStorySlide = () => {
    setCurrentStorySlide((prev) => (prev + 1) % displayStories.length);
  };

  const prevStorySlide = () => {
    setCurrentStorySlide((prev) => (prev - 1 + displayStories.length) % displayStories.length);
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Futura', 'Trebuchet MS', Arial, sans-serif" }}>
      {/* Hero Slider Section - Full screen including header area */}
      <section className="relative w-full h-screen overflow-hidden">
        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            {/* Background Image or Placeholder */}
            {slide.image ? (
              <img
                src={slide.image}
                alt={slide.brideName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 flex items-center justify-center">
                <div className="text-center text-gray-600">
                  <div className="text-8xl mb-4">👰</div>
                  <p className="text-xl tracking-wider">BRIDAL LOOK {index + 1}</p>
                </div>
              </div>
            )}

            {/* Overlay for text visibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40"></div>

            {/* Content - Desktop - Name and Date */}
            <div className="hidden md:flex absolute bottom-20 left-12 flex-col z-10">
              <h2 className="text-white text-5xl font-light tracking-[0.1em] mb-3 drop-shadow-lg" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                {slide.brideName}
              </h2>
              <p className="text-white text-lg tracking-[0.15em] drop-shadow-lg">
                {slide.makeupDate}
              </p>
            </div>

            {/* Content - Mobile - Name and Date */}
            <div className="md:hidden absolute bottom-32 left-0 right-0 text-center px-6 z-10">
              <h2 className="text-white text-4xl font-light tracking-[0.08em] mb-3 drop-shadow-lg" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                {slide.brideName}
              </h2>
              <p className="text-white text-base tracking-[0.1em] drop-shadow-lg">
                {slide.makeupDate}
              </p>
            </div>
          </div>
        ))}

        {/* Top Category Bar - Desktop - Overlaid on image */}
        <div className="hidden md:block absolute top-0 left-0 right-0 bg-[#FFE4E9]/90 py-3 z-20">
          <div className="container mx-auto px-4">
            <div className="flex justify-center gap-16">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`text-sm tracking-[0.2em] font-medium transition-all ${
                    activeCategory === category
                      ? "text-gray-900 border-b-2 border-gray-900 pb-1"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Category Bar - Overlaid on image */}
        <div className="md:hidden absolute top-0 left-0 right-0 bg-[#FFE4E9]/90 py-3 z-20">
          <div className="flex justify-center gap-6 px-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`text-xs tracking-[0.15em] font-medium whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? "text-gray-900 border-b-2 border-gray-900 pb-1"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Header - Desktop - Overlaid on image */}
        <header className="hidden md:block absolute top-12 left-0 right-0 bg-transparent py-4 z-20">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              {/* Left - Empty for balance */}
              <div className="w-32"></div>

              {/* Center Logo */}
              <div className="text-center cursor-pointer" onClick={() => navigate("/")}>
                <div className="text-4xl font-bold tracking-wider text-white drop-shadow-lg">M</div>
                <div className="text-xs tracking-[0.3em] mt-1 text-white drop-shadow-lg">MUAS ACADEMY</div>
              </div>

              {/* Right Icons */}
              <div className="flex items-center gap-6 w-32 justify-end">
                <Phone 
                  className="w-5 h-5 text-white cursor-pointer hover:text-gray-200 drop-shadow-lg" 
                  onClick={() => window.open("tel:9773682082")} 
                />
                <MessageCircle 
                  className="w-5 h-5 text-white cursor-pointer hover:text-gray-200 drop-shadow-lg" 
                  onClick={() => window.open("https://wa.me/919773682082")} 
                />
                <Instagram 
                  className="w-5 h-5 text-white cursor-pointer hover:text-gray-200 drop-shadow-lg" 
                  onClick={() => window.open("https://www.instagram.com/aradhanashreyamakeup")} 
                />
              </div>
            </div>
          </div>
        </header>

        {/* Header - Mobile - Overlaid on image */}
        <header className="md:hidden absolute top-12 left-0 right-0 bg-transparent py-4 z-20">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              {/* Menu Icon */}
              <Menu className="w-6 h-6 text-white drop-shadow-lg" />

              {/* Center Logo */}
              <div className="text-center cursor-pointer" onClick={() => navigate("/")}>
                <div className="text-3xl font-bold tracking-wider text-white drop-shadow-lg">M</div>
                <div className="text-[10px] tracking-[0.25em] text-white drop-shadow-lg">MUAS ACADEMY</div>
              </div>

              {/* Right Icons */}
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-white drop-shadow-lg" onClick={() => window.open("tel:9773682082")} />
                <MessageCircle className="w-5 h-5 text-white drop-shadow-lg" onClick={() => window.open("https://wa.me/919773682082")} />
                <Instagram className="w-5 h-5 text-white drop-shadow-lg" onClick={() => window.open("https://www.instagram.com/aradhanashreyamakeup")} />
              </div>
            </div>
          </div>
        </header>

        {/* Navigation Arrows - Desktop */}
        <button
          onClick={prevSlide}
          className="hidden md:flex absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 items-center justify-center text-white/70 hover:text-white transition-colors z-10"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
        <button
          onClick={nextSlide}
          className="hidden md:flex absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 items-center justify-center text-white/70 hover:text-white transition-colors z-10"
        >
          <ChevronRight className="w-8 h-8" />
        </button>

        {/* Navigation Arrows - Mobile */}
        <button
          onClick={prevSlide}
          className="md:hidden absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white z-10"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="md:hidden absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white z-10"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide
                  ? "bg-white"
                  : "bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </section>

      {/* My Last 5 Brides Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl tracking-[0.2em] text-gray-800 mb-3" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
            MY LAST 5 BRIDES
          </h2>
          <p className="text-xs md:text-sm text-gray-500 tracking-wider mb-8 max-w-xl mx-auto">
            check out the beauties with Best Makeup In Ranchi by Aradhana Shreya
          </p>

          {/* Brides Slider - Single Bride with Arrows - Desktop */}
          <div className="hidden md:block relative max-w-4xl mx-auto overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(calc(50% - 200px - ${currentBrideSlide * 400}px))` }}
            >
              {brides.map((bride, index) => (
                <div
                  key={bride.id}
                  className={`flex-shrink-0 w-[400px] px-2 cursor-pointer group transition-all duration-500 ${
                    index === currentBrideSlide ? 'scale-100 opacity-100' : 'scale-90 opacity-50'
                  }`}
                >
                  <div className="relative h-[528px] overflow-hidden rounded-lg shadow-xl">
                    {bride.image ? (
                      <img
                        src={bride.image}
                        alt={bride.brideName}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 flex items-center justify-center">
                        <div className="text-7xl">👰</div>
                      </div>
                    )}
                    {/* Overlay with name and date */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-5">
                      <p className="text-white text-lg tracking-[0.15em] font-medium">{bride.brideName}</p>
                      <p className="text-white/70 text-sm tracking-wider">{bride.makeupDate}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows for Brides - Desktop */}
            <button
              onClick={prevBrideSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center shadow-xl z-10 transition-all hover:scale-110"
            >
              <ChevronLeft className="w-7 h-7 text-gray-800" />
            </button>
            <button
              onClick={nextBrideSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center shadow-xl z-10 transition-all hover:scale-110"
            >
              <ChevronRight className="w-7 h-7 text-gray-800" />
            </button>
          </div>

          {/* Brides Slider - One by One - Mobile (height increased by 10% = h-[385px]) */}
          <div className="md:hidden relative overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out gap-1"
              style={{ transform: `translateX(calc(50% - 120px - ${currentBrideSlide * 241}px))` }}
            >
              {brides.map((bride, index) => (
                <div
                  key={bride.id}
                  className="flex-shrink-0 w-60 cursor-pointer"
                  onClick={() => setFullscreenBride(bride)}
                >
                  <div className="relative h-[385px] overflow-hidden">
                    {bride.image ? (
                      <img
                        src={bride.image}
                        alt={bride.brideName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 flex items-center justify-center">
                        <div className="text-6xl">👰</div>
                      </div>
                    )}
                    {/* Overlay with name and date */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <p className="text-white text-sm tracking-[0.1em] font-medium">{bride.brideName}</p>
                      <p className="text-white/70 text-xs tracking-wider">{bride.makeupDate}</p>
                    </div>
                    {/* Tap to view indicator */}
                    <div className="absolute top-2 right-2 bg-black/50 text-white text-[10px] px-2 py-1 rounded tracking-wider">
                      TAP TO VIEW
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows for Brides - Mobile */}
            <button
              onClick={(e) => { e.stopPropagation(); prevBrideSlide(); }}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg z-10"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextBrideSlide(); }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg z-10"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
          </div>

          {/* Fullscreen Bride Image Modal - Mobile Only */}
          {fullscreenBride && (
            <div 
              className="md:hidden fixed inset-0 z-50 bg-black flex items-center justify-center"
              onClick={() => setFullscreenBride(null)}
            >
              {/* Close Button */}
              <button
                onClick={() => setFullscreenBride(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center z-10"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              {/* Full Screen Image */}
              <div className="w-full h-full flex items-center justify-center p-4">
                {fullscreenBride.image ? (
                  <img
                    src={fullscreenBride.image}
                    alt={fullscreenBride.brideName}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 flex items-center justify-center">
                    <div className="text-9xl">👰</div>
                  </div>
                )}
              </div>

              {/* Bride Info at Bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6 text-center">
                <p className="text-white text-lg tracking-[0.15em] font-medium">{fullscreenBride.brideName}</p>
                <p className="text-white/70 text-sm tracking-wider mt-1">{fullscreenBride.makeupDate}</p>
              </div>
            </div>
          )}

          {/* Dots Indicator for Brides */}
          <div className="flex justify-center gap-2 mt-6">
            {brides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBrideSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentBrideSlide
                    ? "bg-gray-800"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

        </div>
      </section>

      {/* Real Bride Stories Video Slider Section - MOVED HERE */}
      <section className="py-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="container mx-auto px-4">
          {/* Heading Above Slider */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl tracking-[0.2em] text-gray-800 mb-3" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
              REAL BRIDES STORIES
            </h2>
            <p className="text-xs md:text-sm text-gray-500 tracking-wider max-w-xl mx-auto">
              A beautiful journey of love and celebration
            </p>
          </div>

          {/* Slider Container */}
          <div className="relative max-w-6xl mx-auto">
            {/* Main Slider with Side Previews */}
            <div className="flex items-center justify-center gap-4">
              {/* Left Arrow */}
              <button
                onClick={prevStorySlide}
                className="hidden md:flex w-12 h-12 items-center justify-center text-gray-400 hover:text-gray-600 transition-colors z-10"
                aria-label="Previous story"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>

              {/* Left Preview Card (Desktop only) - Static image/thumbnail, no video */}
              <div className="hidden lg:block w-48 h-80 rounded-2xl overflow-hidden opacity-40 transform scale-90 -mr-8 z-0">
                {displayStories[(currentStorySlide - 1 + displayStories.length) % displayStories.length]?.video ? (
                  <img
                    src={(() => {
                      const v = displayStories[(currentStorySlide - 1 + displayStories.length) % displayStories.length].video;
                      return v.startsWith('/api') ? `${BACKEND_URL}${v}` : v;
                    })()}
                    className="w-full h-full object-cover"
                    alt="Previous bride"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-pink-200 to-rose-300" />
                )}
              </div>

              {/* Main Center Card - No video playback, just thumbnail with button */}
              <div className="relative w-full max-w-md md:max-w-lg h-[420px] md:h-[480px] rounded-3xl overflow-hidden bg-black shadow-2xl z-10">
                {/* Static Image/Thumbnail - No video playback */}
                {currentStory?.video ? (
                  <img
                    src={currentStory.video.startsWith('/api') ? `${BACKEND_URL}${currentStory.video}` : currentStory.video}
                    className="absolute inset-0 w-full h-full object-cover"
                    alt={currentStory.brideName}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800" />
                )}

                {/* Watch Full Video Button - Transparent, Bottom Right Corner */}
                {currentStory?.fullVideo && (
                  <button
                    onClick={() => setFullscreenStoryVideo(currentStory)}
                    className="absolute bottom-20 right-4 md:bottom-24 md:right-6 z-20 bg-transparent border border-white/60 hover:border-white hover:bg-white/10 text-white px-4 py-2 rounded text-xs tracking-[0.1em] transition-all flex items-center gap-2"
                    style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
                  >
                    <Play className="w-3 h-3" /> WATCH FULL VIDEO
                  </button>
                )}

                {/* Bride Name and Date Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 bg-gradient-to-t from-black/80 to-transparent z-10">
                  <p className="text-white text-lg md:text-xl tracking-[0.15em] font-medium" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                    {currentStory?.brideName || "BRIDE NAME"}
                  </p>
                  <p className="text-white/70 text-sm tracking-wider mt-1">
                    {currentStory?.makeupDate || "Makeup Date"}
                  </p>
                </div>
              </div>

              {/* Right Preview Card (Desktop only) - Static image/thumbnail, no video */}
              <div className="hidden lg:block w-48 h-80 rounded-2xl overflow-hidden opacity-40 transform scale-90 -ml-8 z-0">
                {displayStories[(currentStorySlide + 1) % displayStories.length]?.video ? (
                  <img
                    src={(() => {
                      const v = displayStories[(currentStorySlide + 1) % displayStories.length].video;
                      return v.startsWith('/api') ? `${BACKEND_URL}${v}` : v;
                    })()}
                    className="w-full h-full object-cover"
                    alt="Next bride"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-200 to-pink-300" />
                )}
              </div>

              {/* Right Arrow */}
              <button
                onClick={nextStorySlide}
                className="hidden md:flex w-12 h-12 items-center justify-center text-gray-400 hover:text-gray-600 transition-colors z-10"
                aria-label="Next story"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </div>

            {/* Mobile Navigation Arrows */}
            <div className="flex md:hidden justify-center gap-8 mt-6">
              <button
                onClick={prevStorySlide}
                className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 border border-gray-300 rounded-full"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextStorySlide}
                className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 border border-gray-300 rounded-full"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-10">
            <Button
              onClick={() => navigate('/more-bride-stories')}
              className="bg-gray-900 hover:bg-gray-800 text-white tracking-[0.15em] px-10 py-6 text-sm"
              style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
            >
              MORE BRIDE STORIES
            </Button>
          </div>
        </div>
      </section>

      {/* Fullscreen Story Video Modal */}
      {fullscreenStoryVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black"
        >
          {/* Close Button */}
          <button
            onClick={() => setFullscreenStoryVideo(null)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center z-20"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Full Screen Video - No space, fills entire screen */}
          <video
            src={(() => {
              const v = fullscreenStoryVideo.fullVideo;
              return v.startsWith('/api') ? `${BACKEND_URL}${v}` : v;
            })()}
            className="w-full h-full object-contain bg-black"
            controls
            autoPlay
            playsInline
          />
        </div>
      )}

      {/* Price List Section - Slider with Multiple Headings */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Desktop View - Two Halves */}
          <div className="hidden md:grid md:grid-cols-2 gap-0 max-w-6xl mx-auto">
            {/* Left Half - Image */}
            <div className="relative h-[600px] overflow-hidden">
              {priceListImage ? (
                <img
                  src={priceListImage}
                  alt="Bridal"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-pink-100 via-rose-100 to-purple-100 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <div className="text-8xl mb-4">👰</div>
                    <p className="text-lg tracking-wider">BRIDAL IMAGE</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Half - Price List Slider */}
            <div className="relative h-[600px] bg-[#1a1a1a] flex flex-col justify-center px-12 overflow-hidden">
              {/* Heading with Navigation Arrows */}
              <div className="flex items-center justify-center gap-4 mb-8">
                {displayHeadings.length > 1 && (
                  <button
                    onClick={prevPriceSlide}
                    className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}
                <h2 className="text-2xl tracking-[0.2em] text-white text-center min-w-[200px]" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                  {currentHeading?.name || "PACKAGES"}
                </h2>
                {displayHeadings.length > 1 && (
                  <button
                    onClick={nextPriceSlide}
                    className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Price Items for Current Heading */}
              <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2">
                {currentHeading?.items?.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-start border-b border-white/20 pb-4"
                  >
                    <div className="flex-1">
                      <h3 className="text-lg text-white tracking-[0.1em]" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                        {item.name}
                      </h3>
                      <p className="text-sm text-white/60 mt-1 tracking-wide">
                        {item.detail}
                      </p>
                    </div>
                    <div className="text-right ml-8">
                      <p className="text-xl text-white tracking-wider" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                        Rs. {item.price}
                      </p>
                    </div>
                  </div>
                ))}
                {(!currentHeading?.items || currentHeading.items.length === 0) && (
                  <p className="text-white/50 text-center py-8">No items in this category</p>
                )}
              </div>

              {/* Dots Indicator */}
              {displayHeadings.length > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {displayHeadings.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPriceSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentPriceSlide
                          ? "bg-white"
                          : "bg-white/30 hover:bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={() => window.open("https://wa.me/919773682082?text=Hello%20Aradhana%2C%20I%20want%20to%20Enquire%20About%20Makeup%20Prices", "_blank")}
                className="mt-8 mx-auto px-8 py-3 border border-white/50 text-white tracking-[0.15em] text-sm bg-transparent hover:bg-white/10 transition-all flex items-center gap-2"
                style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
              >
                <MessageCircle className="w-4 h-4" />
                CHAT WITH ARADHANA
              </button>
            </div>
          </div>

          {/* Mobile View - Stacked */}
          <div className="md:hidden">
            {/* Image */}
            <div className="relative h-80 overflow-hidden">
              {priceListImage ? (
                <img
                  src={priceListImage}
                  alt="Bridal"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-pink-100 via-rose-100 to-purple-100 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <div className="text-6xl mb-2">👰</div>
                    <p className="text-sm tracking-wider">BRIDAL IMAGE</p>
                  </div>
                </div>
              )}
            </div>

            {/* Price List Slider */}
            <div className="bg-[#1a1a1a] py-8 px-4">
              {/* Heading with Navigation Arrows */}
              <div className="flex items-center justify-center gap-2 mb-6">
                {displayHeadings.length > 1 && (
                  <button
                    onClick={prevPriceSlide}
                    className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <h2 className="text-base tracking-[0.12em] text-white text-center flex-1" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                  {currentHeading?.name || "PACKAGES"}
                </h2>
                {displayHeadings.length > 1 && (
                  <button
                    onClick={nextPriceSlide}
                    className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Price Items for Current Heading */}
              <div className="space-y-3 max-h-[280px] overflow-y-auto px-2">
                {currentHeading?.items?.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-start border-b border-white/20 pb-3"
                  >
                    <div className="flex-1">
                      <h3 className="text-sm text-white tracking-[0.08em]" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                        {item.name}
                      </h3>
                      <p className="text-xs text-white/60 mt-1 tracking-wide">
                        {item.detail}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-base text-white tracking-wider" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                        Rs. {item.price}
                      </p>
                    </div>
                  </div>
                ))}
                {(!currentHeading?.items || currentHeading.items.length === 0) && (
                  <p className="text-white/50 text-center py-4 text-sm">No items in this category</p>
                )}
              </div>

              {/* Dots Indicator */}
              {displayHeadings.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {displayHeadings.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPriceSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentPriceSlide
                          ? "bg-white"
                          : "bg-white/30 hover:bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={() => window.open("https://wa.me/919773682082?text=Hello%20Aradhana%2C%20I%20want%20to%20Enquire%20About%20Makeup%20Prices", "_blank")}
                className="mt-6 w-full py-3 border border-white/50 text-white tracking-[0.12em] text-xs bg-transparent hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
              >
                <MessageCircle className="w-4 h-4" />
                CHAT WITH ARADHANA
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* INSPIRING BRIDE LOOKS Section - Rolling Carousel */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Heading */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl tracking-[0.2em] text-gray-800 mb-3" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
              INSPIRING BRIDE LOOKS FOR 2026
            </h2>
            <p className="text-xs md:text-sm text-gray-500 tracking-wider max-w-xl mx-auto">
              Confused For Your Big Day? Get Inspired By Bride Looks done on our Models
            </p>
          </div>

          {/* Desktop Carousel - 5 cards visible with center larger - Portrait cards increased by 30% */}
          <div className="hidden md:block relative">
            <div className="flex items-center justify-center gap-5 py-8">
              {[-2, -1, 0, 1, 2].map((offset) => {
                const index = (currentInspiringSlide + offset + displayInspiringLooks.length) % displayInspiringLooks.length;
                const look = displayInspiringLooks[index];
                const isCenter = offset === 0;
                const isEdge = Math.abs(offset) === 2;
                
                return (
                  <div
                    key={`${look.id}-${offset}`}
                    className={`flex-shrink-0 transition-all duration-500 ease-in-out ${
                      isCenter ? 'w-80 h-[500px] z-20 scale-110' : 
                      isEdge ? 'w-52 h-80 opacity-40 z-0' : 
                      'w-68 h-[420px] opacity-70 z-10'
                    }`}
                    style={!isCenter && !isEdge ? { width: '272px', height: '420px' } : {}}
                  >
                    <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-xl">
                      {look.image ? (
                        <img
                          src={look.image}
                          alt={look.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 flex items-center justify-center">
                          <div className="text-5xl">👰</div>
                        </div>
                      )}
                      {/* Name overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                        <p className="text-white text-base tracking-[0.1em] text-center" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                          {look.name}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Carousel - 3 cards visible - Portrait cards increased by 30% */}
          <div className="md:hidden relative">
            <div className="flex items-center justify-center gap-3 py-4">
              {[-1, 0, 1].map((offset) => {
                const index = (currentInspiringSlide + offset + displayInspiringLooks.length) % displayInspiringLooks.length;
                const look = displayInspiringLooks[index];
                const isCenter = offset === 0;
                
                return (
                  <div
                    key={`${look.id}-${offset}-mobile`}
                    className={`flex-shrink-0 transition-all duration-500 ease-in-out ${
                      isCenter ? 'w-52 h-72 z-20 scale-110' : 'w-36 h-56 opacity-50 z-10'
                    }`}
                  >
                    <div className="relative w-full h-full rounded-xl overflow-hidden shadow-lg">
                      {look.image ? (
                        <img
                          src={look.image}
                          alt={look.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 flex items-center justify-center">
                          <div className="text-3xl">👰</div>
                        </div>
                      )}
                      {/* Name overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <p className="text-white text-sm tracking-[0.08em] text-center" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                          {look.name}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TRENDY HAIRSTYLES FOR BRIDES Section (Renamed from Inspiring Model Brides) */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Heading - Above Video */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl tracking-[0.2em] text-gray-800 mb-3" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
              TRENDY HAIRSTYLES FOR BRIDES
            </h2>
            <p className="text-xs md:text-sm text-gray-500 tracking-wider">
              Hairstyle Completes a Bridal Makeup
            </p>
          </div>

          {/* Desktop View */}
          <div className="hidden md:block">
            <div className="max-w-5xl mx-auto">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer group">
                <div className="aspect-[16/9] bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900">
                  {inspiringVideo ? (
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="absolute inset-0 w-full h-full object-cover"
                    >
                      <source src={inspiringVideo.startsWith('/api') ? `${BACKEND_URL}${inspiringVideo}` : inspiringVideo} type="video/mp4" />
                    </video>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white/50">
                        <div className="text-6xl mb-2">🎬</div>
                        <p className="text-sm tracking-wider">VIDEO</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Explore CTA - Center */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button 
                      onClick={() => navigate('/trendy-hairstyles')}
                      className="bg-transparent border-2 border-white text-white px-10 py-3 tracking-[0.15em] text-sm hover:bg-white/10 transition-all flex items-center gap-2"
                      style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
                    >
                      EXPLORE <span className="text-lg">›</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <div className="relative rounded-2xl overflow-hidden shadow-xl cursor-pointer group mx-4">
              <div className="aspect-[4/3] bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900">
                {inspiringVideo ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  >
                    <source src={inspiringVideo.startsWith('/api') ? `${BACKEND_URL}${inspiringVideo}` : inspiringVideo} type="video/mp4" />
                  </video>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white/50">
                      <div className="text-5xl mb-2">🎬</div>
                      <p className="text-xs tracking-wider">VIDEO</p>
                    </div>
                  </div>
                )}
                
                {/* Explore CTA - Center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button 
                    onClick={() => navigate('/trendy-hairstyles')}
                    className="bg-transparent border-2 border-white text-white px-6 py-2 tracking-[0.12em] text-xs hover:bg-white/10 transition-all flex items-center gap-1"
                    style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
                  >
                    EXPLORE <span>›</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE MAKEUP PROCESS Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Heading */}
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl tracking-[0.2em] text-gray-800" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                THE MAKEUP PROCESS
              </h2>
            </div>

            {/* Accordion Items */}
            <div className="space-y-4 mb-10 max-w-3xl mx-auto">
              {[
                { id: 1, title: "Initial Consultation", content: "Content coming soon..." },
                { id: 2, title: "Measurements", content: "Content coming soon..." },
                { id: 3, title: "Design Process", content: "Content coming soon..." },
                { id: 4, title: "Delivery", content: "Content coming soon..." }
              ].map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <button
                    onClick={() => setExpandedProcess(expandedProcess === item.id ? null : item.id)}
                    className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm md:text-base tracking-[0.1em] text-gray-800" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                      {item.title}
                    </span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                        expandedProcess === item.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedProcess === item.id && (
                    <div className="px-6 pb-4 text-gray-600 text-sm">
                      {item.content}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Video Section with CTA - Increased by 30% */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <div className="aspect-[16/9] md:aspect-[16/8] bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900" style={{ minHeight: '450px' }}>
                {makeupProcessVideo ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  >
                    <source src={makeupProcessVideo.startsWith('/api') ? `${BACKEND_URL}${makeupProcessVideo}` : makeupProcessVideo} type="video/mp4" />
                  </video>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white/50">
                      <div className="text-7xl mb-2">🎬</div>
                      <p className="text-base tracking-wider">PROCESS VIDEO</p>
                    </div>
                  </div>
                )}
                
                {/* CTA Button - Transparent */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    onClick={() => window.open("https://wa.me/919773682082?text=Hello, I'd like to book an appointment")}
                    className="bg-transparent border-2 border-white text-white px-10 py-4 tracking-[0.15em] text-base hover:bg-white/10 transition-all"
                    style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
                  >
                    BOOK AN APPOINTMENT
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE ARTIST HERSELF Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Heading */}
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl tracking-[0.2em] text-gray-800" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                THE ARTIST HERSELF
              </h2>
            </div>

            {/* Intro Text */}
            <div className="text-center mb-8">
              <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
                {artistBio.intro || "Meet Aradhana, a passionate makeup artist with years of experience in bridal makeup. Her journey in the beauty industry began with a simple love for enhancing natural beauty and has grown into a celebrated career."}
              </p>
            </div>

            {/* Photo Slider - Portrait orientation with bigger size */}
            <div className="relative mb-8 flex justify-center">
              <div className="w-80 md:w-96 h-[480px] md:h-[580px] rounded-2xl overflow-hidden shadow-xl bg-gray-100">
                {artistImages.length > 0 ? (
                  <img
                    src={artistImages[currentArtistSlide]?.image}
                    alt="The Artist"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <div className="text-7xl mb-2">👩‍🎨</div>
                      <p className="text-base tracking-wider">ARTIST PHOTOS</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Arrows */}
              {artistImages.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentArtistSlide((prev) => (prev - 1 + artistImages.length) % artistImages.length)}
                    className="absolute left-4 md:left-1/4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-800" />
                  </button>
                  <button
                    onClick={() => setCurrentArtistSlide((prev) => (prev + 1) % artistImages.length)}
                    className="absolute right-4 md:right-1/4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-800" />
                  </button>
                </>
              )}
            </div>

            {/* Dots Indicator */}
            {artistImages.length > 1 && (
              <div className="flex justify-center gap-2 mb-8">
                {artistImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentArtistSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentArtistSlide ? "bg-gray-800 w-6" : "bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Outro Text */}
            <div className="text-center">
              <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
                {artistBio.outro || "With a keen eye for detail and a commitment to excellence, Aradhana has transformed countless brides into the best versions of themselves. Her work is not just makeup—it's an art form that celebrates individuality and beauty."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OUR LOCATION Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl tracking-[0.2em] text-gray-800 text-center mb-12" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
            OUR LOCATION
          </h2>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto items-center">
            {/* Left Side - Address Text */}
            <div className="space-y-4 text-center md:text-left">
              <p className="text-gray-600 text-sm tracking-wider" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                We are located at
              </p>
              <h3 className="text-gray-800 text-2xl md:text-3xl tracking-[0.1em] leading-tight" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                Sahajanand Chowk, Harmu, Ranchi
              </h3>
              <div className="pt-4">
                <p className="text-gray-500 text-xs tracking-wider underline mb-2" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
                  Address
                </p>
                <p className="text-gray-600 text-sm tracking-wide">
                  C-127, Harmu Housing Colony, Harmu Ranchi 834002
                </p>
              </div>
            </div>

            {/* Right Side - Google Map (Desktop) */}
            <div className="hidden md:block rounded-2xl overflow-hidden shadow-xl h-80">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3663.0779413928234!2d85.30622931496908!3d23.344135484786746!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f4e1e5e5e5e5e5%3A0x573656250116139851!2sMUAS%20Academy!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MUAS Academy Location"
              ></iframe>
            </div>

            {/* Mobile View - CTA Button */}
            <div className="md:hidden text-center">
              <Button
                className="bg-gray-900 hover:bg-gray-800 text-white tracking-[0.15em] px-10 py-5"
                onClick={() => window.open("https://share.google/mAeU7ULFywbXvrOfK", "_blank")}
                style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}
              >
                CHECK GOOGLE LOCATION
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Strip */}
      <section className="py-12 bg-[#FFE4E9]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl md:text-2xl tracking-[0.15em] text-gray-800 mb-6" style={{ fontFamily: "'Futura', 'Trebuchet MS', sans-serif" }}>
            BOOK YOUR APPOINTMENT
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              className="bg-gray-900 hover:bg-gray-800 text-white tracking-[0.1em] px-8"
              onClick={() => window.open("tel:9773682082")}
            >
              <Phone className="w-4 h-4 mr-2" />
              CALL NOW
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white tracking-[0.1em] px-8"
              onClick={() => window.open("https://wa.me/919773682082?text=Hello, I'm interested in bridal makeup services")}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WHATSAPP
            </Button>
            <Button
              variant="outline"
              className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white tracking-[0.1em] px-8"
              onClick={() => window.open("https://www.instagram.com/aradhanashreyamakeup")}
            >
              <Instagram className="w-4 h-4 mr-2" />
              INSTAGRAM
            </Button>
          </div>
        </div>
      </section>

      {/* CSS for font */}
      <style>{`
        @import url('https://fonts.cdnfonts.com/css/futura-pt');
        
        * {
          font-family: 'Futura PT', 'Futura', 'Trebuchet MS', Arial, sans-serif;
        }
      `}</style>

      {/* Footer */}
      <Footer />
    </div>
  );
}
